import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { cn } from '@cobliteam/ui/utils';
import { type ComponentProps, createElement, type MouseEvent, useCallback } from 'react';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { type ExtraProps, Streamdown } from 'streamdown';
import { COBLI_LINK_HREF, dispatchCobliAction, isCobliLink } from '../lib/cobliLinkActions';
import { useFloatingChat } from '../store';
import './streamdown.css';

export type AIChatMarkdownVariant = 'chat' | 'assistant';

export type AIChatMarkdownComponents = NonNullable<ComponentProps<typeof Streamdown>['components']>;
type MarkdownComponentProps<Tag extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[Tag] & ExtraProps;

interface AIChatMarkdownProps {
  children: string;
  components?: AIChatMarkdownComponents;
  isAnimating?: boolean;
  mode?: ComponentProps<typeof Streamdown>['mode'];
  variant?: AIChatMarkdownVariant;
}

const markdownSizeClasses: Record<AIChatMarkdownVariant, Record<string, string>> = {
  chat: {},
  assistant: {
    h1: 'text-2xl font-bold',
    h2: 'text-xl font-semibold',
    h3: 'text-lg font-semibold',
    h4: 'text-base font-semibold',
    h5: 'text-sm font-semibold',
  },
};

const getInternalHref = (href: string) => {
  if (!href) return '';
  if (href.startsWith('/')) return href;

  if (typeof window === 'undefined') return null;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return href;
  }

  return null;
};

const MarkdownLink = ({ className, href = '', node: _node, rel, target: _target, ...props }: MarkdownComponentProps<'a'>) => {
  const linkClassName = cn('wrap-anywhere font-medium text-primary underline', className);
  const internalHref = getInternalHref(href);

  if (internalHref !== null) {
    return <Link {...props} to={internalHref} rel={rel} className={linkClassName} data-streamdown='link' />;
  }

  return <a {...props} href={href} rel={rel} className={linkClassName} data-streamdown='link' />;
};

const createHeading = <Tag extends 'h1' | 'h2' | 'h3' | 'h4' | 'h5'>(Tag: Tag, className?: string) => {
  return ({ className: currentClassName, node: _node, ...props }: MarkdownComponentProps<'h1'>) => createElement(Tag, { ...props, className: cn(className, currentClassName) });
};

const AIChatMarkdown = ({ children, isAnimating = true, mode = 'streaming', variant = 'chat', components }: AIChatMarkdownProps) => {
  const isAssistant = variant === 'assistant';
  const sizeClasses = isAssistant ? markdownSizeClasses[variant] : {};

  const { openIntercom } = useChatWidget();
  const setIntercomOpen = useFloatingChat((s) => s.setIntercomOpen);

  const handleCobliLinkClick = useCallback(
    (href: string, event: MouseEvent<HTMLAnchorElement>) => {
      if (!isCobliLink(href)) return false;
      event.preventDefault();
      switch (href) {
        case COBLI_LINK_HREF.OPEN_BLIP_CHAT:
          dispatchCobliAction('open-blip-chat');
          openIntercom();
          setIntercomOpen(true);
          break;
        default:
          // Unknown cobli:// action — swallow click so the browser doesn't
          // try to navigate to an unknown scheme. New actions must be added
          // to COBLI_LINK_HREF in lib/cobliLinkActions.ts.
          break;
      }
      return true;
    },
    [openIntercom, setIntercomOpen]
  );

  // cobli:// action links are intercepted here (they need component-scoped
  // hooks); everything else delegates to MarkdownLink, which routes internal
  // same-origin links through react-router and renders external ones as <a>.
  const anchorOverride = ({ href = '', onClick, ...props }: MarkdownComponentProps<'a'>) => {
    if (isCobliLink(href)) {
      const { node: _node, className, ...anchorProps } = props;
      return (
        <a
          {...anchorProps}
          href={href}
          className={cn('wrap-anywhere font-medium text-primary underline', className)}
          data-streamdown='link'
          onClick={(event) => {
            if (handleCobliLinkClick(href, event)) return;
            onClick?.(event);
          }}
        />
      );
    }

    return <MarkdownLink {...props} href={href} onClick={onClick} />;
  };

  const defaultComponents: AIChatMarkdownComponents = {
    a: anchorOverride,
    ...(isAssistant
      ? {
          h1: createHeading('h1', sizeClasses.h1),
          h2: createHeading('h2', sizeClasses.h2),
          h3: createHeading('h3', sizeClasses.h3),
          h4: createHeading('h4', sizeClasses.h4),
          h5: createHeading('h5', sizeClasses.h5),
        }
      : {}),
  };

  return (
    <Streamdown
      className='ai-chat-streamdown'
      linkSafety={{ enabled: false }}
      mode={mode}
      isAnimating={isAnimating}
      animated
      remarkPlugins={[remarkGfm]}
      components={{ ...defaultComponents, ...components }}
    >
      {children}
    </Streamdown>
  );
};

export default AIChatMarkdown;
